import React from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { ExpedienteDespacho } from '../PaginaDespachoPortuario'

interface Props {
  expediente: ExpedienteDespacho
  mostrarSelloVerde?: boolean
}

export const HojaPedimentoSAT: React.FC<Props> = ({ expediente, mostrarSelloVerde = true }) => {
  const anio = '26'
  const aduana = '16' // Manzanillo
  const patente = expediente.actores.agenteAduanal.patente || '3450'
  const consecutivo = expediente.folio.replace(/[^0-9]/g, '').slice(-7).padStart(7, '4')
  const numPedimento = `${anio}  ${aduana}  ${patente}  ${consecutivo}`

  const esExportacion = expediente.tipoOperacion === 'EXPORTACION'
  const clavePedimento = expediente.regimen === 'IR_RFE' ? 'IR' : expediente.regimen === 'IN_IMMEX' ? 'IN' : 'A1'
  const regimenAbrev = expediente.regimen === 'IR_RFE' ? 'RFE' : expediente.regimen === 'IN_IMMEX' ? 'ITR' : 'DFE'
  const tipoOperacionNum = esExportacion ? '2' : '1'

  const valorUSD = expediente.documentos.factura.valorUSD || 68400
  const tipoCambio = 19.852
  const valorMXN = Math.round(valorUSD * tipoCambio)
  const dta = esExportacion ? 425 : 850
  const prv = 310
  const cnt = 95
  const iva = esExportacion ? 0 : Math.round(valorMXN * 0.16)
  const totalContribuciones = dta + prv + cnt + iva

  const satFirmaDigital = `||${numPedimento}|${expediente.actores.emisor.rfc}|${valorUSD}|${totalContribuciones}|2026-10-04T12:00:00|SAT-SEMARNAT-VUCEM-ANAM||`
  const satSelloHash = 'e8b7c4a19f023d88a7c2901f4b2389e1a90c2834b71f92e8a1c900e42d8819bc34fa76e9'

  return (
    <div className="relative w-full bg-[#fdfdfc] text-slate-900 shadow-2xl rounded-sm p-6 sm:p-10 font-sans border border-slate-300 print:border-none print:shadow-none print:p-4 text-[10px] sm:text-[11px] leading-tight select-none">
      {/* Marca de Agua de Seguridad Oficial */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.035] overflow-hidden">
        <div className="text-[120px] font-black uppercase text-slate-900 -rotate-45 select-none tracking-widest whitespace-nowrap">
          ANAM SAT SHCP
        </div>
      </div>

      {/* Encabezado Oficial del SAT / ANAM */}
      <div className="border-b-2 border-slate-900 pb-2 mb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-0.5">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-600">
              SECRETARÍA DE HACIENDA Y CRÉDITO PÚBLICO
            </p>
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-800">
              AGENCIA NACIONAL DE ADUANAS DE MÉXICO (ANAM)
            </p>
            <h1 className="text-base sm:text-lg font-black tracking-tight text-slate-950 font-mono">
              PEDIMENTO DE COMERCIO EXTERIOR
            </h1>
            <p className="text-[9px] font-mono text-slate-500">
              FORMATO OFICIAL APROBADO EN EL ANEXO 22 DE LAS REGLAS GENERALES DE COMERCIO EXTERIOR
            </p>
          </div>

          <div className="text-right flex flex-col items-end">
            <span className="text-[9px] font-bold uppercase bg-slate-900 text-white px-2 py-0.5 rounded-sm">
              EJEMPLAR: CONTRIBUYENTE
            </span>
            <span className="font-mono text-[9px] text-slate-600 mt-1">PÁG. 1 DE 1</span>
            {/* Simulación de Código de Barras SAT */}
            <div className="mt-1 flex flex-col items-center">
              <div className="h-7 w-44 flex items-center justify-between px-1 bg-white border border-slate-400">
                {Array.from({ length: 42 }).map((_, i) => (
                  <div
                    key={i}
                    className={`h-full ${i % 3 === 0 ? 'w-1 bg-black' : i % 5 === 0 ? 'w-1.5 bg-black' : 'w-0.5 bg-black'}`}
                  />
                ))}
              </div>
              <span className="font-mono text-[8px] text-slate-700 tracking-wider">
                *{numPedimento.replace(/\s+/g, '')}*
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Cuadrícula Principal: Datos de Encabezado */}
      <div className="border-2 border-slate-900 mb-3 divide-y-2 divide-slate-900">
        {/* Fila 1 */}
        <div className="grid grid-cols-12 divide-x divide-slate-900 bg-slate-100/60 font-mono">
          <div className="col-span-4 p-1.5">
            <span className="block text-[8px] font-sans font-bold uppercase text-slate-600">NUM. PEDIMENTO</span>
            <span className="font-bold text-xs sm:text-sm tracking-wider text-slate-950">{numPedimento}</span>
          </div>
          <div className="col-span-2 p-1.5">
            <span className="block text-[8px] font-sans font-bold uppercase text-slate-600">T. OPERACIÓN</span>
            <span className="font-bold text-xs text-slate-950">
              {tipoOperacionNum} ({esExportacion ? 'EXP' : 'IMP'})
            </span>
          </div>
          <div className="col-span-2 p-1.5">
            <span className="block text-[8px] font-sans font-bold uppercase text-slate-600">CVE. PEDIM</span>
            <span className="font-bold text-xs text-slate-950">{clavePedimento}</span>
          </div>
          <div className="col-span-2 p-1.5">
            <span className="block text-[8px] font-sans font-bold uppercase text-slate-600">RÉGIMEN</span>
            <span className="font-bold text-xs text-slate-950">{regimenAbrev}</span>
          </div>
          <div className="col-span-2 p-1.5">
            <span className="block text-[8px] font-sans font-bold uppercase text-slate-600">DESTINO/ORIGEN</span>
            <span className="font-bold text-xs text-slate-950">9 (INTERIOR)</span>
          </div>
        </div>

        {/* Fila 2 */}
        <div className="grid grid-cols-12 divide-x divide-slate-900 font-mono">
          <div className="col-span-3 p-1.5">
            <span className="block text-[8px] font-sans font-bold uppercase text-slate-600">TIPO CAMBIO</span>
            <span className="font-bold text-slate-900">{tipoCambio.toFixed(4)}</span>
          </div>
          <div className="col-span-3 p-1.5">
            <span className="block text-[8px] font-sans font-bold uppercase text-slate-600">PESO BRUTO (KG)</span>
            <span className="font-bold text-slate-900">{expediente.contenedor.pesoBrutoKg.toLocaleString()}.00</span>
          </div>
          <div className="col-span-3 p-1.5">
            <span className="block text-[8px] font-sans font-bold uppercase text-slate-600">ADUANA E/S</span>
            <span className="font-bold text-slate-900">160 (MANZANILLO, COL.)</span>
          </div>
          <div className="col-span-3 p-1.5">
            <span className="block text-[8px] font-sans font-bold uppercase text-slate-600">MEDIO TRANSPORTE</span>
            <span className="font-bold text-slate-900">1 (MARÍTIMO)</span>
          </div>
        </div>

        {/* Fila 3: Importador / Exportador */}
        <div className="grid grid-cols-12 divide-x divide-slate-900 p-2 bg-white">
          <div className="col-span-8 pr-2">
            <span className="block text-[8px] font-bold uppercase text-slate-600">
              DATOS DEL {esExportacion ? 'EXPORTADOR (EMISOR)' : 'IMPORTADOR'}
            </span>
            <p className="font-bold text-xs text-slate-950 uppercase">{expediente.actores.emisor.nombre}</p>
            <p className="text-[9px] text-slate-700 font-mono">
              RFC: <strong className="text-slate-900">{expediente.actores.emisor.rfc}</strong> | PAÍS:{' '}
              {expediente.actores.emisor.pais}
            </p>
            <p className="text-[9px] text-slate-500 truncate">
              DOMICILIO: AV. INDUSTRIAL DEL PUERTO #450, PARQUE LOGÍSTICO MANZANILLO, COLIMA.
            </p>
          </div>

          <div className="col-span-4 pl-2 font-mono">
            <span className="block text-[8px] font-sans font-bold uppercase text-slate-600">VALOR COMERCIAL USD</span>
            <span className="font-bold text-sm text-slate-950">${valorUSD.toLocaleString()}.00</span>
            <span className="block text-[8px] font-sans font-bold uppercase text-slate-600 mt-1">VALOR ADUANA MXN</span>
            <span className="font-bold text-xs text-slate-900">${valorMXN.toLocaleString()}.00</span>
          </div>
        </div>

        {/* Fila 4: Destinatario / Proveedor Extranjero */}
        <div className="grid grid-cols-12 divide-x divide-slate-900 p-2 bg-slate-50/70">
          <div className="col-span-8 pr-2">
            <span className="block text-[8px] font-bold uppercase text-slate-600">
              DATOS DEL {esExportacion ? 'DESTINATARIO (RECEPTOR)' : 'PROVEEDOR COMPRADOR'}
            </span>
            <p className="font-bold text-xs text-slate-950 uppercase">{expediente.actores.receptor.nombre}</p>
            <p className="text-[9px] text-slate-700 font-mono">
              TAX ID / RFC: <strong className="text-slate-900">{expediente.actores.receptor.rfc}</strong> | PAÍS:{' '}
              {expediente.actores.receptor.pais}
            </p>
            <p className="text-[9px] text-slate-500 truncate">DOMICILIO: {expediente.actores.receptor.domicilio}</p>
          </div>

          <div className="col-span-4 pl-2">
            <span className="block text-[8px] font-bold uppercase text-slate-600">INCOTERM APLICABLE</span>
            <span className="font-bold text-xs text-slate-900 font-mono">
              {expediente.documentos.factura.incoterm || 'FOB MANZANILLO'}
            </span>
            <span className="block text-[8px] font-bold uppercase text-slate-600 mt-1">FACTURA FISCAL</span>
            <span className="font-mono font-bold text-[10px] text-slate-800">
              {expediente.documentos.factura.folio} ({expediente.documentos.factura.fecha})
            </span>
          </div>
        </div>
      </div>

      {/* Cuadro de Liquidación de Contribuciones */}
      <div className="border border-slate-900 mb-3">
        <div className="bg-slate-900 text-white px-2 py-0.5 font-bold text-[8px] uppercase tracking-wider flex justify-between">
          <span>CUADRO DE LIQUIDACIÓN DE GRAVÁMENES Y CONTRIBUCIONES</span>
          <span>ANEXO 22 - RGCE</span>
        </div>
        <div className="grid grid-cols-6 divide-x divide-slate-300 font-mono text-center text-[9px] border-b border-slate-300 bg-slate-100/50 font-bold py-1">
          <div>CONCEPTO</div>
          <div>F. PAGO</div>
          <div>IMPORTE MXN</div>
          <div>CONCEPTO</div>
          <div>F. PAGO</div>
          <div>IMPORTE MXN</div>
        </div>
        <div className="grid grid-cols-6 divide-x divide-slate-300 font-mono text-[9px] divide-y divide-slate-200">
          <div className="p-1 font-bold">1. DTA (DERECHO TRÁMITE)</div>
          <div className="p-1 text-center">0 (EFECTIVO)</div>
          <div className="p-1 text-right font-bold pr-2">${dta}.00</div>
          <div className="p-1 font-bold">2. PRV (PREVALIDACIÓN)</div>
          <div className="p-1 text-center">0 (EFECTIVO)</div>
          <div className="p-1 text-right font-bold pr-2">${prv}.00</div>

          <div className="p-1 font-bold">3. CNT (CONT. COMPLEM)</div>
          <div className="p-1 text-center">0 (EFECTIVO)</div>
          <div className="p-1 text-right font-bold pr-2">${cnt}.00</div>
          <div className="p-1 font-bold">4. IVA COMERCIO EXT.</div>
          <div className="p-1 text-center">0 (EFECTIVO)</div>
          <div className="p-1 text-right font-bold pr-2">${iva.toLocaleString()}.00</div>
        </div>
        <div className="bg-slate-900 text-white font-mono flex items-center justify-between px-3 py-1 text-xs font-bold">
          <span className="font-sans text-[9px] uppercase tracking-wider">TOTAL EFECTIVO PAGADO EN BANCO AUTORIZADO:</span>
          <span className="text-sm font-black text-amber-300">${totalContribuciones.toLocaleString()}.00 MXN</span>
        </div>
      </div>

      {/* Bloque de Contenedores, Sellos y Transporte */}
      <div className="border border-slate-900 mb-3">
        <div className="bg-slate-200 px-2 py-0.5 font-bold text-[8px] uppercase tracking-wider text-slate-800 border-b border-slate-900">
          DATOS DEL CONTENEDOR, CANDADOS FISCALES Y MEDIO DE TRANSPORTE
        </div>
        <div className="grid grid-cols-12 divide-x divide-slate-300 font-mono text-[9px] p-2 bg-white">
          <div className="col-span-3">
            <span className="block text-[8px] font-sans font-bold text-slate-500 uppercase">NO. CONTENEDOR</span>
            <span className="font-bold text-xs text-slate-950">{expediente.contenedor.numero}</span>
            <span className="block text-[8px] text-slate-600">TIPO: {expediente.contenedor.tipo}</span>
          </div>

          <div className="col-span-3 pl-2">
            <span className="block text-[8px] font-sans font-bold text-slate-500 uppercase">CANDADOS / SELLOS FISCALES</span>
            <span className="font-bold text-slate-900 text-[10px]">{expediente.contenedor.sellos}</span>
            <span className="block text-[8px] text-emerald-700 font-bold">VERIFICADO EN GARITA</span>
          </div>

          <div className="col-span-3 pl-2">
            <span className="block text-[8px] font-sans font-bold text-slate-500 uppercase">BUQUE / VIAJE</span>
            <span className="font-bold text-slate-900 text-[10px] uppercase">{expediente.buque.nombre}</span>
            <span className="block text-[8px] text-slate-600">VIAJE: {expediente.buque.viaje}</span>
          </div>

          <div className="col-span-3 pl-2">
            <span className="block text-[8px] font-sans font-bold text-slate-500 uppercase">TRANSPORTISTA & CAAT</span>
            <span className="font-bold text-slate-900 text-[10px] truncate block">
              {expediente.actores.transportista.razonSocial}
            </span>
            <span className="block text-[8px] text-slate-600">
              CAAT: {expediente.actores.transportista.caat} | PLACAS: {expediente.actores.transportista.placas}
            </span>
          </div>
        </div>
      </div>

      {/* Partida Arancelaria Oficial TIGIE */}
      <div className="border border-slate-900 mb-3">
        <div className="bg-slate-200 px-2 py-0.5 font-bold text-[8px] uppercase tracking-wider text-slate-800 border-b border-slate-900">
          PARTIDAS ARANCELARIAS (TIGIE / NICO)
        </div>
        <table className="w-full text-left font-mono text-[9px] border-collapse">
          <thead>
            <tr className="bg-slate-100 border-b border-slate-300 font-bold text-slate-700">
              <th className="p-1">SEC</th>
              <th className="p-1">FRACCIÓN / NICO</th>
              <th className="p-1">DESCRIPCIÓN DE MERCANCÍA</th>
              <th className="p-1 text-center">UMC</th>
              <th className="p-1 text-right">CANTIDAD</th>
              <th className="p-1 text-right">VALOR COMERCIAL</th>
              <th className="p-1 text-center">IGI/IGE</th>
              <th className="p-1 text-center">IVA</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            <tr>
              <td className="p-1 font-bold">001</td>
              <td className="p-1 font-bold text-blue-900">
                {expediente.regimen === 'IR_RFE' ? '0804.40.01.00' : '2208.90.03.00'}
              </td>
              <td className="p-1 uppercase text-slate-800 max-w-[220px]">
                {expediente.titulo.slice(0, 50)}... EMPACADO EN {expediente.documentos.packingList.bultos} BULTOS.
              </td>
              <td className="p-1 text-center">01 (KG)</td>
              <td className="p-1 text-right font-bold">{expediente.documentos.packingList.pesoNetoKg.toLocaleString()}</td>
              <td className="p-1 text-right font-bold">${valorUSD.toLocaleString()} USD</td>
              <td className="p-1 text-center text-emerald-700 font-bold">EX.</td>
              <td className="p-1 text-center text-emerald-700 font-bold">0%</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Pie de Pedimento: Cadena Original, QR y Sello Digital */}
      <div className="border-2 border-slate-900 p-2 bg-slate-50/70 font-mono text-[8px] relative">
        <div className="grid grid-cols-12 gap-3 items-center">
          {/* QR Oficial SAT */}
          <div className="col-span-2 flex flex-col items-center justify-center p-1 bg-white border border-slate-300 rounded">
            <QRCodeSVG value={`https://verificapedimento.sat.gob.mx/qr?num=${numPedimento.replace(/\s+/g, '')}&rfc=${expediente.actores.emisor.rfc}&total=${totalContribuciones}`} size={64} />
            <span className="text-[6px] font-sans font-bold text-slate-500 mt-0.5">SAT VUCEM QR</span>
          </div>

          {/* Sello Digital y Cadena Original */}
          <div className="col-span-7 space-y-1">
            <div>
              <span className="font-sans font-bold text-[7px] uppercase text-slate-600 block">
                CADENA ORIGINAL DEL COMPLEMENTO DE CERTIFICACIÓN DIGITAL DEL SAT:
              </span>
              <p className="break-all text-[7px] text-slate-600 leading-tight">
                {satFirmaDigital}
              </p>
            </div>
            <div>
              <span className="font-sans font-bold text-[7px] uppercase text-slate-600 block">
                SELLO DIGITAL DE LA AGENCIA ADUANAL (PATENTE {patente}):
              </span>
              <p className="break-all text-[7px] text-slate-600 leading-tight">
                {satSelloHash}
              </p>
            </div>
            <p className="font-sans text-[7px] text-slate-500">
              AGENTE ADUANAL: {expediente.actores.agenteAduanal.nombre} · ADUANA 160 MANZANILLO.
            </p>
          </div>

          {/* Firma Manuscrita Simulada */}
          <div className="col-span-3 text-center border-l border-slate-300 pl-2">
            <div className="h-9 flex items-center justify-center">
              <span className="font-serif italic text-base text-blue-900 tracking-tighter opacity-80 select-none">
                Lic. F. Méndez A.
              </span>
            </div>
            <div className="border-t border-slate-700 pt-0.5">
              <span className="font-sans font-bold text-[7px] uppercase text-slate-700 block">
                FIRMA ELECTRÓNICA AVANZADA
              </span>
              <span className="text-[7px] text-slate-500 font-mono">PAT. 3450 / ANAM</span>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* SELLO DE GOMA REALISTA: MODULACIÓN SAT / SEMÁFORO ADUANAL */}
        {/* ========================================================================= */}
        {mostrarSelloVerde && (
          <div className="absolute right-6 top-[-48px] pointer-events-none transform -rotate-6">
            <div className="px-4 py-2 rounded-lg border-2 border-dashed border-emerald-700 bg-emerald-50/80 backdrop-blur-sm text-emerald-800 shadow-md">
              <div className="text-center font-mono space-y-0.5">
                <p className="text-[8px] font-black tracking-widest uppercase">MECANISMO DE SELECCIÓN AUTOMATIZADA</p>
                <div className="flex items-center justify-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-emerald-600 animate-pulse" />
                  <p className="text-xs font-black tracking-wider uppercase text-emerald-900">
                    DESADUANAMIENTO LIBRE
                  </p>
                </div>
                <p className="text-[7px] font-bold tracking-tight text-emerald-700">
                  ADUANA DE MANZANILLO (160) · CÓDIGO B48 LIBERADO
                </p>
                <p className="text-[6.5px] text-emerald-600">
                  FECHA: 04/OCT/2026 14:22:09 HRS · FOLIO: SAT-{numPedimento.slice(-6)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Nota Legal de Pie de Hoja */}
      <div className="mt-2 text-center text-[7px] font-mono text-slate-400">
        ESTE DOCUMENTO ES UNA SIMULACIÓN EDUCATIVA HIPERREALISTA DE PEDIMENTO ADUANAL CONFORME A LA LEY ADUANERA MEXICANA Y REGLAS GENERALES DE COMERCIO EXTERIOR VIGENTES EN EL PUERTO DE MANZANILLO.
      </div>
    </div>
  )
}
